import { put, takeLatest, call, all, select } from 'redux-saga/effects'
import { MeetingInviteStatusEnum } from '@medentee/enums'

import { API, APIData, api } from 'services/api'
import { toastDefaultOptions } from 'globalConstants'
import { toast } from 'App/components/ToastContainer'
import { ongoingCallsNormalize } from 'store/calls/calls.normalize'
import { handleError } from 'api/utils'
import { joinExternalMeeting, TMeetingAdmission } from 'api/meetings'
import { State } from 'redux/rootReducer'
import { isInCallRoomSelector } from 'store/calls/calls.selectors'

import i18n from '../../i18n'

import {
  acceptMeetingInviteError,
  acceptMeetingInviteRequest,
  acceptMeetingInviteSuccess,
  cancelMeetingError,
  cancelMeetingRequest,
  cancelMeetingSuccess,
  getMeetingsError,
  getMeetingsRequest,
  getMeetingsSuccess,
  rejectMeetingInviteError,
  rejectMeetingInviteRequest,
  rejectMeetingInviteSuccess,
  scheduleMeetingError,
  scheduleMeetingRequest,
  scheduleMeetingSuccess,
  enterExternalMeetingRequest,
  enterExternalMeetingSuccess,
  enterExternalMeetingError
} from './meetings.actions'
import {
  SCHEDULE_MEETING_REQUEST,
  GET_MEETINGS_REQUEST,
  ACCEPT_MEETING_INVITE_REQUEST,
  CANCEL_MEETING_REQUEST,
  REJECT_MEETING_INVITE_REQUEST,
  ENTER_EXTERNAL_MEETING_REQUEST
} from '.'
import { meetingsNormalize } from './meetings.normalization'
import { TMeetingsList } from './meetings.types'

const MEETINGS_PAGE_SIZE = 100

function* scheduleMeetingSaga({
  payload: { chatId, onSuccess, ...data }
}: ReturnType<typeof scheduleMeetingRequest>) {
  try {
    const response: APIData<{ id: string }> = yield call(
      api.post,
      API.SCHEDULE_MEETING(chatId),
      data
    )

    if (onSuccess) {
      yield call(onSuccess, response.data.id)
    }

    yield put(scheduleMeetingSuccess())

    yield toast.success(i18n.t('common.toast.canceledMeeting'), toastDefaultOptions)
  } catch (e) {
    yield handleError(e)
    yield put(scheduleMeetingError(e))
  }
}

function* getMeetingsSaga({ payload }: ReturnType<typeof getMeetingsRequest>) {
  try {
    const page = payload?.page ?? 0

    const { meetings, ongoingCalls } = yield all({
      meetings: call(api.get, API.MEETINGS, {
        params: { page, showBy: MEETINGS_PAGE_SIZE }
      }),
      ongoingCalls: call(api.get, API.CALLS_ONGOING)
    })

    const held: TMeetingsList = page ? yield select((state: State) => state.meetings.list) : {}
    const merged = [...Object.values(held), ...meetings.data.results]

    yield put(
      getMeetingsSuccess({
        meetings: meetingsNormalize(merged),
        ongoingCalls: ongoingCallsNormalize(ongoingCalls.data),
        total: meetings.data.total
      })
    )
  } catch (e) {
    yield handleError(e)
    yield put(getMeetingsError(e))
  }
}

function* cancelMeetingSaga({
  payload: { id, onSuccess }
}: ReturnType<typeof cancelMeetingRequest>) {
  try {
    yield call(api.delete, API.CANCEL_MEETING(id))

    if (onSuccess) {
      yield call(onSuccess)
    }

    yield put(cancelMeetingSuccess({ processingId: id }))
    yield toast.success(i18n.t('common.toast.canceledMeeting'), toastDefaultOptions)
  } catch (e) {
    yield handleError(e)
    yield put(cancelMeetingError({ ...e, processingId: id }))
  }
}

function* acceptMeetingInviteSaga({
  payload: { id, onSuccess }
}: ReturnType<typeof acceptMeetingInviteRequest>) {
  try {
    yield call(api.patch, API.UPDATE_MEETING_INVITES(id), {
      status: MeetingInviteStatusEnum.ACCEPTED
    })

    yield put(getMeetingsRequest())
    yield put(acceptMeetingInviteSuccess({ processingId: id }))
    if (onSuccess) {
      yield call(onSuccess)
    }
  } catch (e) {
    yield handleError(e)
    yield put(acceptMeetingInviteError({ ...e, processingId: id }))
  }
}

function* rejectMeetingInviteSaga({
  payload: { id, onSuccess, onError }
}: ReturnType<typeof rejectMeetingInviteRequest>) {
  try {
    yield call(api.patch, API.UPDATE_MEETING_INVITES(id), {
      status: MeetingInviteStatusEnum.REJECTED
    })

    if (onSuccess) {
      yield call(onSuccess)
    }
    yield put(rejectMeetingInviteSuccess({ processingId: id }))
  } catch (e) {
    yield handleError(e)
    yield put(rejectMeetingInviteError({ ...e, processingId: id }))

    if (onError) {
      yield call(onError)
    }
  }
}

function* enterExternalMeetingSaga({ payload }: ReturnType<typeof enterExternalMeetingRequest>) {
  try {
    const isInRoom: boolean = yield select((state: State) => isInCallRoomSelector(state))

    if (isInRoom) {
      yield call(
        toast.info,

        i18n.t('chat.call.alreadyInCall'),

        toastDefaultOptions
      )

      return
    }

    const admission: TMeetingAdmission = payload.admission
      ? payload.admission
      : yield call(joinExternalMeeting, payload.code)

    yield put(enterExternalMeetingSuccess({ admission }))
  } catch (e) {
    yield put(enterExternalMeetingError(e))

    yield handleError(e)
  }
}

export function* meetingsSaga() {
  yield takeLatest(SCHEDULE_MEETING_REQUEST, scheduleMeetingSaga)
  yield takeLatest(GET_MEETINGS_REQUEST, getMeetingsSaga)
  yield takeLatest(ENTER_EXTERNAL_MEETING_REQUEST, enterExternalMeetingSaga)
  yield takeLatest(ACCEPT_MEETING_INVITE_REQUEST, acceptMeetingInviteSaga)
  yield takeLatest(CANCEL_MEETING_REQUEST, cancelMeetingSaga)
  yield takeLatest(REJECT_MEETING_INVITE_REQUEST, rejectMeetingInviteSaga)
}
